import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Icon from '../../components/Icon';

const FEATURES = [
  {
    icon: 'calendar_month',
    title: 'Shared Calendar',
    desc: 'Everyone sees work shifts, school runs, and appointments in one place.',
    accent: '#b39ddb',
  },
  {
    icon: 'format_list_bulleted',
    title: 'Smart Lists',
    desc: 'Shopping lists that update live. Tick things off from the shop floor.',
    accent: '#e9c46a',
  },
  {
    icon: 'restaurant',
    title: 'Meal Planner',
    desc: 'Plan the week, save favourite recipes, auto-build your shopping list.',
    accent: '#4fc3f7',
  },
  {
    icon: 'account_balance_wallet',
    title: 'Family Budget',
    desc: 'Track spending, set limits, see where the money actually goes.',
    accent: '#81c784',
  },
  {
    icon: 'check_circle',
    title: 'Tasks & Chores',
    desc: 'Assign jobs, set deadlines, rotate fairly. No more arguments.',
    accent: '#ff8a65',
  },
  {
    icon: 'chat_bubble',
    title: 'Family Chat',
    desc: 'A private space for your lot. Photos, quick messages, no noise.',
    accent: '#80cbc4',
  },
];

const MORE_FEATURES = [
  { icon: 'local_hospital', label: 'Health Records' },
  { icon: 'school', label: 'School Hub' },
  { icon: 'inventory_2', label: 'Pantry Tracker' },
  { icon: 'folder_open', label: 'Documents' },
  { icon: 'star', label: 'Special Days' },
  { icon: 'location_on', label: 'Family Map' },
  { icon: 'contacts', label: 'Contact Book' },
  { icon: 'receipt_long', label: 'Subscriptions' },
];

const LandingPage: React.FC = () => {
  const history = useHistory();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const go = (path: string) => history.push(path);

  return (
    <div className="land">
      {/* ── Nav ── */}
      <nav className={`land-nav ${scrolled || mobileMenuOpen ? 'land-nav--scrolled' : ''}`}>
        <div className="land-container land-nav__inner">
          <a href="/" className="land-nav__brand">
            <img src="/icon-nobg.png" alt="FamHearth logo" className="land-nav__logo"  loading="lazy"/>
            <span className="land-nav__wordmark">FamHearth</span>
          </a>

          <div className="land-nav__links">
            <a href="#features" className="land-nav__link">Features</a>
            <a href="#how-it-works" className="land-nav__link">How it works</a>
            <a href="#pricing" className="land-nav__link">Pricing</a>
            <button onClick={() => go('/login')} className="land-nav__link">Log in</button>
            <button onClick={() => go('/register')} className="land-nav__cta">
              Get started<span className="land-nav__cta-arrow">→</span>
            </button>
          </div>

          <button
            className="land-nav__hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="land-nav__mobile-menu">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="land-nav__mobile-link">Features</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="land-nav__mobile-link">How it works</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="land-nav__mobile-link">Pricing</a>
            <button onClick={() => { setMobileMenuOpen(false); go('/login'); }} className="land-nav__mobile-link">Log in</button>
            <button onClick={() => { setMobileMenuOpen(false); go('/register'); }} className="land-nav__cta land-nav__cta--full">
              Get started free
            </button>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="land-hero">
        <div className="land-hero__glow" />
        <div className="land-container land-hero__inner">
          <h1 className="land-hero__title">
            Family life,<br />without the chaos.
          </h1>
          <p className="land-hero__subtitle">
            Meals, money, chores, calendars and everything in between. One app your whole family actually uses.
          </p>
          <div className="land-hero__actions">
            <button onClick={() => go('/register')} className="land-btn land-btn--primary land-btn--lg">
              Get started free
            </button>
            <a href="#how-it-works" className="land-btn land-btn--ghost land-btn--lg">
              See how it works
            </a>
          </div>
        </div>
      </section>

      {/* ── Trust strip ── */}
      <section className="land-trust">
        <div className="land-container land-trust__inner">
          <div className="land-trust__item">
            <span className="land-trust__value">20+</span>
            <span className="land-trust__label">features</span>
          </div>
          <div className="land-trust__divider" />
          <div className="land-trust__item">
            <span className="land-trust__value">3</span>
            <span className="land-trust__label">platforms: web, iOS, Android</span>
          </div>
          <div className="land-trust__divider" />
          <div className="land-trust__item">
            <span className="land-trust__value">Free</span>
            <span className="land-trust__label">to start</span>
          </div>
          <div className="land-trust__divider" />
          <div className="land-trust__item">
            <span className="land-trust__value">∞</span>
            <span className="land-trust__label">family members</span>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="land-features">
        <div className="land-container">
          <p className="land-label">Features</p>
          <h2 className="land-heading">
            Everything your family needs.
          </h2>
          <p className="land-subheading">
            Built for real households, not tech demos.
          </p>

          <div className="land-features__grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="land-fcard">
                <div className="land-fcard__icon" style={{ color: f.accent, background: `${f.accent}15` }}>
                  <Icon name={f.icon} size={24} color={f.accent} />
                </div>
                <h3 className="land-fcard__title">{f.title}</h3>
                <p className="land-fcard__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── More features (pill cloud) ── */}
      <section className="land-more">
        <div className="land-container land-more__inner">
          <p className="land-label">And so much more</p>
          <h2 className="land-heading land-heading--sm">15+ features. One app.</h2>
          <div className="land-more__pills">
            {MORE_FEATURES.map((f) => (
              <div key={f.label} className="land-more__pill">
                <Icon name={f.icon} size={16} />
                {f.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how-it-works" className="land-how">
        <div className="land-container">
          <p className="land-label">How it works</p>
          <h2 className="land-heading">Up and running in 60 seconds</h2>

          <div className="land-how__steps">
            <div className="land-how__step">
              <div className="land-how__num">01</div>
              <h3 className="land-how__step-title">Create your account</h3>
              <p className="land-how__step-desc">Sign up with your email in under a minute. No credit card needed.</p>
            </div>
            <div className="land-how__step">
              <div className="land-how__num">02</div>
              <h3 className="land-how__step-title">Start your family</h3>
              <p className="land-how__step-desc">Name your household, get a code, invite everyone.</p>
            </div>
            <div className="land-how__step">
              <div className="land-how__num">03</div>
              <h3 className="land-how__step-title">Organise everything</h3>
              <p className="land-how__step-desc">Use what you need. Skip what you don't. Simple as that.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="land-pricing">
        <div className="land-container">
          <p className="land-label">Pricing</p>
          <h2 className="land-heading">Simple, honest pricing</h2>
          <p className="land-subheading">Start free. Upgrade when you're ready.</p>

          <div className="land-pricing__cards">
            <div className="land-pricing__card">
              <div className="land-pricing__name">Free</div>
              <div className="land-pricing__price">£0<span>/month</span></div>
              <p className="land-pricing__desc">Everything you need to get going.</p>
              <ul className="land-pricing__list">
                <li>Up to 5 family members</li>
                <li>Calendar, lists &amp; tasks</li>
                <li>Family chat</li>
                <li>Meal planner &amp; recipes</li>
                <li>Budget tracker</li>
              </ul>
              <button onClick={() => go('/register')} className="land-btn land-btn--outline land-btn--full">
                Get started
              </button>
            </div>

            <div className="land-pricing__card land-pricing__card--featured">
              <div className="land-pricing__badge">Most popular</div>
              <div className="land-pricing__name">Premium</div>
              <div className="land-pricing__price">£4.99<span>/month</span></div>
              <p className="land-pricing__desc">The full family experience.</p>
              <ul className="land-pricing__list">
                <li>Unlimited family members</li>
                <li>Everything in Free</li>
                <li>Family locator &amp; safe zones</li>
                <li>Document storage (10 GB)</li>
                <li>Priority support</li>
                <li>Cancel any time</li>
              </ul>
              <button onClick={() => go('/register')} className="land-btn land-btn--primary land-btn--full">
                Start free trial
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="land-cta">
        <div className="land-cta__glow" />
        <div className="land-container land-cta__inner">
          <h2 className="land-cta__title">Your family deserves this.</h2>
          <p className="land-cta__desc">
            Join families already using FamHearth to stay organised and connected.
          </p>
          <button onClick={() => go('/register')} className="land-btn land-btn--primary land-btn--lg">
            Get started free
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="land-footer">
        <div className="land-container land-footer__inner">
          <div className="land-footer__top">
            <div className="land-footer__brand">
              <img src="/icon-nobg.png" alt="FamHearth logo" className="land-footer__logo"  loading="lazy"/>
              <span className="land-footer__wordmark">FamHearth</span>
            </div>
            <div className="land-footer__cols">
              <div className="land-footer__col">
                <div className="land-footer__col-title">Product</div>
                <a href="#features" className="land-footer__link">Features</a>
                <a href="#pricing" className="land-footer__link">Pricing</a>
                <a href="#how-it-works" className="land-footer__link">How it works</a>
              </div>
              <div className="land-footer__col">
                <div className="land-footer__col-title">Company</div>
                <a href="/features" className="land-footer__link">About</a>
                <a href="/privacy" className="land-footer__link">Privacy</a>
                <a href="/terms" className="land-footer__link">Terms</a>
              </div>
              <div className="land-footer__col">
                <div className="land-footer__col-title">Connect</div>
                <a href="mailto:hello@famhearth.com" className="land-footer__link">Contact</a>
                <a href="mailto:privacy@famhearth.com" className="land-footer__link">Privacy enquiries</a>
                <a href="mailto:support@famhearth.com" className="land-footer__link">Support</a>
              </div>
            </div>
          </div>
          <div className="land-footer__bottom">
            <p>&copy; {new Date().getFullYear()} Joro Services Ltd (company no. 14079588). Registered in England and Wales. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
